<template>
  <v-dialog
    v-model="dialog"
    persistent
    transition="dialog-bottom-transition"
    max-width="1000"
    v-if="isDataLoaded"
  >
    <!--begin::Modal dialog-->
    <div class="bg-white poppins">
      <!--begin::Modal header-->
      <div class="modal-header py-4 align-center">
        <h4 class="mb-0 font-weight-bolder">{{ headerText }}</h4>
        <!--begin::Close-->
        <div
          class="btn btn-sm btn-icon btn-active-light-info"
          @click="toggleModal"
        >
          <span class="svg-icon">
            <v-icon size="20">mdi-close</v-icon>
          </span>
        </div>
        <!--end::Close-->
      </div>
      <!--end::Modal header-->
      <!--begin::Modal body-->
      <div class="modal-body d-flex flex-column scroll-y mx-5 py-7">
        <form class="row ma-0">
          <div class="col-12 col-sm-6 h-75px">
            <v-text-field
              v-model="formData.name"
              :label="$t('name')"
              dense
              outlined
              clearable
              :error-messages="nameErrors"
              @blur="$v.formData.name.$touch()"
            />
          </div>

          <div class="col-12 col-sm-6 h-75px">
            <v-text-field
              v-model="formData.priority"
              :label="$t('priority')"
              type="number"
              dense
              outlined
              clearable
              :error-messages="priorityErrors"
              @blur="$v.formData.priority.$touch()"
            />
          </div>
          <div class="col-12 col-sm-4 h-75px">
            <v-autocomplete
              v-model="formData.status"
              :label="$t('status')"
              :items="statusValues"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
              :error-messages="statusErrors"
              @blur="$v.formData.status.$touch()"
            />
          </div>
          <div class="col-12 col-sm-4 h-75px">
            <v-autocomplete
              v-model="formData.type_id"
              :label="$t('type')"
              :items="serverData.types"
              @change="onTypeChange"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
            />
          </div>
          <div class="col-12 col-sm-4 h-75px">
            <v-autocomplete
              v-model="formData.apply_type_id"
              :label="$t('apply_type')"
              :items="serverData.apply_types"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
              :error-messages="apply_type_idErrors"
              @blur="$v.formData.apply_type_id.$touch()"
            />
          </div>
          <div class="col-12 h-75px" v-if="formData.apply_type_id === 2">
            <v-combobox
              v-model="formData.carriers"
              :label="$t('carriers')"
              :items="carriers"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
              multiple
              small-chips
              deletable-chips
              :error-messages="carriersErrors"
              @blur="$v.formData.carriers.$touch()"
            >
              <template v-slot:prepend-item>
                <v-list-item ripple @click="toggle">
                  <v-list-item-action>
                    <v-icon>
                      {{ icon }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title class="font-size-sm">
                      Select All
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
            </v-combobox>
          </div>

          <div class="col-12 h-75px">
            <v-text-field
              v-model="formData.description"
              :label="$t('description')"
              dense
              outlined
              clearable
            />
          </div>

          <!-- <div class="col-12 col-sm-6 col-md-4 h-75px">
            <v-autocomplete
              v-model="formData.actions"
              :label="$t('actions')"
              :items="actions"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable

              :error-messages="actionErrors"
              @blur="$v.formData.actions.$touch()"
            />
          </div> -->
        </form>

        <div class="mt-4 px-3 w-100">
          <RuleBuilder
            :query="query"
            :options="serverData.operands"
            :userId="userId"
            ref="ruleBuilder"
          />
        </div>
        <div class="mt-4 px-3 w-100">
          <ActionBuilder
            :query="formData.actions"
            :options="serverData.actions"
            :operators="serverData"
            :userId="userId"
            ref="actionBuilder"
          />
        </div>

        <!--begin::Actions-->
        <div class="my-4 h-75px d-flex flex-grow-1 align-end justify-center">
          <button
            type="reset"
            class="btn btn-light mr-3 px-5 py-3 ls1"
            @click="resetFormData"
          >
            Clear
          </button>
          <button
            type="submit"
            class="btn btn-info px-5 py-3 ls1"
            @click="submitForm"
          >
            {{ submitButtonText }}
          </button>
        </div>
        <!--end::Actions-->
      </div>
      <!--end::Modal body-->
    </div>
    <!--end::Modal dialog-->
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import swalEdited from "@/core/services/AlertServices/SwalEdited";
import swalAdded from "@/core/services/AlertServices/SwalAdded";
import RuleBuilder from "@/own/components/shipping/shipmentCreationRules/ruleBuilder/RuleBuilder";
import ActionBuilder from "@/own/components/shipping/shipmentCreationRules/actionBuilder/ActionBuilder";
import { SET_COURIER_TYPE } from "@/core/services/store/shipmentCreationRules.module";
import Swal from "sweetalert2";

export default {
  name: "ActionsDialog",
  props: ["refresher", "pageLoader"],
  mixins: [validationMixin],
  components: { RuleBuilder, ActionBuilder },
  validations: {
    formData: {
      carriers: {
        required: requiredIf(function () {
          return this.formData.apply_type_id === 2;
        }),
      },
      name: { required },
      status: { required },
      priority: { required },
      // actions: { required },
      apply_type_id: { required },
    },
  },
  data: () => ({
    dialog: false,
    serverData: null,
    clear_counter: false,
    formData: {
      carriers: null, //++
      name: null, //++
      priority: null, //++
      status: null, //++
      description: null, //++
      // actions: 1,
      actions: [],
      type_id: 1,
      apply_type_id: null,
    },
    query: {
      condition: "and",
      rules: [],
    },
  }),
  methods: {
    checkActionsAreUnique() {
      for (let i = 0; i < this.formData.actions.length - 1; i++) {
        for (let j = i + 1; j < this.formData.actions.length; j++) {
          if (this.formData.actions[i].id == this.formData.actions[j].id) {
            return false;
          }
        }
      }
      return true;
    },
    toggleModal() {
      if (this.dialog) {
        this.resetFormData();
        this.serverData = null;
        this.clear_counter = false;
      } else {
        this.loadDataFromServer();
      }
      this.dialog = !this.dialog;
    },
    resetFormData() {
      this.formData = {
        carriers: null,
        name: null,
        priority: null,
        status: null,
        description: null,
        // actions: 1,
        actions: [],
        type_id: 1,
        apply_type_id: null,
      };
      this.query = {
        condition: "and",
        rules: [],
      };
    },
    // When populating edit data (called after you get the stored data)
    populateEditData(actions) {
      this.formData.actions = actions.map((action) => ({
        id: action.id,
        extra_data: action.extra_data === "null" ? null : action.extra_data,
        value: this.mapActionValue(action),
      }));
    },

    mapActionValue(action) {
      if (!action.value) return null;

      if (action.type === "files") {
        if (Array.isArray(action.value)) {
          // Convert each URL string → { isExisting, url, name }
          return action.value.map((url) => ({
            isExisting: true,
            url,
            name: url.split("/").pop(),
          }));
        }
        // else {
        //   return {
        //     isExisting: true,
        //     url: action.value,
        //     name: action.value.split("/").pop(),
        //   };
        // }
      }

      return action.value;
    },
    // mapActionValue(action) {
    //   if (!action.value) return null;
    //
    //   if (action.type === "files") {
    //     // Convert URL strings to objects so UI can display them
    //     // and we can distinguish them from newly uploaded Files
    //     if (Array.isArray(action.value)) {
    //       return action.value.map((url) => this.urlToFileObject(url));
    //     } else {
    //       return this.urlToFileObject(action.value);
    //     }
    //   }
    //
    //   // For non-file types just return value as-is
    //   return action.value;
    // },

    // urlToFileObject(url) {
    //   // Keep URL as a plain object so UI can preview it
    //   // and submitForm can detect it's already uploaded (not a new File)
    //   return {
    //     isExisting: true, // flag to distinguish from new File uploads
    //     url: url,
    //     name: url.split("/").pop(), // extract filename from url
    //   };
    // },
    loadDataFromServer() {
      // this.$store.dispatch(GET_DATA);
      this.pageLoader(true);
      if (this.actionType) {
        let sendData = { id: this.$store.getters.getItemForAction.id };
        return ApiService.post(
          "/shipping/shipment_creation_rules/edit",
          sendData
        )
          .then(({ data }) => {
            if (data?.shipment_creation_rule.condition)
              this.query = JSON.parse(data.shipment_creation_rule.condition);
            delete data.shipment_creation_rule.condition;
            this.serverData = data;
            this.formData = data.shipment_creation_rule;
            // this.formData.actions = data.shipment_creation_rule.actions[0]; //spartanskiyy
            this.formData.carriers = data.carriers.filter((item) =>
              data.shipment_creation_rule.carriers.includes(item.index)
            );
            this.populateEditData(data.shipment_creation_rule.actions);
            // console.log(" this.query", this.query);
            // console.log(" this.formData", this.formData);
          })
          .then(() => {
            this.pageLoader(false);
          })
          .catch(() => {
            this.dialog = false;
            this.pageLoader(false);
          });
      } else {
        ApiService.post("/shipping/shipment_creation_rules/create")
          .then(({ data }) => {
            this.serverData = data;
            this.pageLoader(false);
          })
          .catch(() => {
            this.dialog = false;
            this.pageLoader(false);
          });
      }
    },
    handleFormValidation(fieldName) {
      const errors = [];
      if (!this.$v.formData[fieldName].$dirty) return errors;
      if ("required" in this.$v.formData[fieldName]) {
        !this.$v.formData[fieldName].required &&
          errors.push(this.$t("validation_this_field_is_required"));
      }
      return errors;
    },
    validateActionBuilderData() {
      let isNotValid = false;
      this.formData.actions.forEach((attr) => {
        if (!attr.value) {
          isNotValid = true;
        }
      });
      return isNotValid;
    },
    submitForm() {
      this.$v.$touch();

      if (!this.checkActionsAreUnique()) {
        Swal.fire({
          title: "Warning",
          text: "Actions attributes must be unique!",
          icon: "warning",
        });
        return;
      }

      if (!this.query.rules.length) {
        Swal.fire({
          title: "Warning",
          text: "Please, add at least one rule!",
          icon: "warning",
        });
        return;
      }

      if (this.$v.$invalid || this.$store.getters.getSHIPCREATRULESNotValid) {
        return;
      }

      if (!this.formData.actions.length) {
        Swal.fire({
          title: "Warning",
          text: "Please, add at least one action!",
          icon: "warning",
        });
        return;
      }

      if (this.validateActionBuilderData()) {
        return;
      }

      this.pageLoader(true);

      // Build FormData
      const formData = new FormData();

      // Simple top-level fields
      formData.append("name", this.formData.name);
      formData.append("status", this.formData.status);
      formData.append("condition", JSON.stringify(this.query));
      formData.append("type_id", this.formData.type_id);
      formData.append("apply_type_id", this.formData.apply_type_id);
      formData.append("priority", this.formData.priority);
      formData.append("description", this.formData.description ?? "");

      // Edit mode — append id
      if (this.actionType) {
        formData.append("id", this.$store.getters.getItemForAction.id);
      }

      // Carriers array
      const carriers =
        this.formData.apply_type_id === 2
          ? this.formData.carriers.map((item) => item.index)
          : this.formData.carriers;

      if (carriers && carriers.length) {
        carriers.forEach((carrier, index) => {
          formData.append(`carriers[${index}]`, carrier);
        });
      }

      // Actions array
      this.formData.actions.forEach((action, i) => {
        if (action.id !== undefined) {
          formData.append(`actions[${i}][id]`, action.id);
        }

        if (action.extra_data !== undefined && action.extra_data !== null) {
          formData.append(`actions[${i}][extra_data]`, action.extra_data);
        }

        if (action.value !== undefined && action.value !== null) {
          // Find the selected option to determine type
          const selectedOption = this.serverData.actions.find(
            (opt) => opt.index === action.id
          );
          const type = selectedOption?.type;

          if (type === "files") {
            // Always array, mix of new File objects and existing URLs
            const values = Array.isArray(action.value)
              ? action.value
              : [action.value];

            values.forEach((val, j) => {
              if (val instanceof File) {
                formData.append(`actions[${i}][value][${j}]`, val, val.name);
              } else if (val?.isExisting) {
                formData.append(`actions[${i}][value][${j}]`, val.url);
              }
            });
          } else {
            // dropdown or text
            if (Array.isArray(action.value)) {
              action.value.forEach((val, j) => {
                formData.append(`actions[${i}][value][${j}]`, val);
              });
            } else {
              formData.append(`actions[${i}][value]`, action.value);
            }
          }
        }
      });

      const actionUrl = this.actionType ? "update" : "store";

      ApiService.post(
        `/shipping/shipment_creation_rules/${actionUrl}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      )
        .then(() => {
          if (this.actionType) swalEdited();
          else swalAdded();
          this.toggleModal();
          this.refresher();
        })
        .catch(() => {
          this.pageLoader(false);
        });
    },
    toggle() {
      if (this.selectedAll) {
        this.$set(this.formData, "carriers", null);
      } else {
        let arr = [...this.carriers];
        this.$set(this.formData, "carriers", arr);
      }
    },
    onTypeChange() {
      this.$store.commit(SET_COURIER_TYPE, this.formData.type_id);
    },
  },
  computed: {
    selectedAll() {
      if (Array.isArray(this.formData.carriers)) {
        return this.carriers.length === this.formData.carriers.length;
      } else return false;
    },
    selectedSome() {
      if (Array.isArray(this.formData.carriers)) {
        return this.formData.carriers.length > 0;
      } else return false;
    },
    icon() {
      if (this.selectedAll) return "mdi-checkbox-marked";
      if (this.selectedSome) return "mdi-minus-box-outline";
      return "mdi-checkbox-blank-outline";
    },
    // if true, we are editing item, false we are adding
    actionType: function () {
      return !!this.$store.getters.getItemForAction;
    },
    submitButtonText: function () {
      if (this.actionType) return "Update";
      else return "Submit";
    },
    headerText: function () {
      if (this.actionType) return "Edit Item";
      else return "Add New Item";
    },
    userId: function () {
      let clients = this.query.rules?.find(
        (item) => item.id === "client"
      )?.value;
      return clients ?? this.$store.getters.currentUser.data.id;
    },
    carriers: function () {
      return this.serverData.carriers;
    },
    statusValues: function () {
      return this.serverData.statuses;
    },
    // ruleTypes: function () {
    //   return this.serverData.types;
    // },
    actions: function () {
      return this.serverData.actions;
    },
    carriersErrors: function () {
      return this.handleFormValidation("carriers");
    },
    nameErrors: function () {
      return this.handleFormValidation("name");
    },
    priorityErrors: function () {
      return this.handleFormValidation("priority");
    },
    statusErrors: function () {
      return this.handleFormValidation("status");
    },
    apply_type_idErrors: function () {
      return this.handleFormValidation("apply_type_id");
    },
    isDataLoaded() {
      return !!this.serverData;
    },
  },
  // watch: {
  // "formData.type_id": function () {
  //   this.query = {
  //     condition: "and",
  //     rules: [],
  //   };
  // },
  // },
};
</script>
